/** 应用状态 */
export type AppStatus = 'development' | 'published' | 'offline'

/**
 * 运行平台 = 清单 distBundle.platform 的 key（如 win32-x64 / darwin-arm64 / darwin-x64），
 * 按架构区分，一个 key 一个子包槽。本期只有桌面端。
 */
export type Platform = `win32-${string}` | `darwin-${string}`

/**
 * 平台家族：列表 ?platform= 筛选、下架 / 回滚结果 affected_platforms / switched_platforms 用这个，
 * 不是清单 key。
 */
export type PlatformFamily = 'windows' | 'macos'

/** 开发者平台上的一个「应用」 */
export interface AppInfo {
  id: string
  name: string
  description: string
  icon: string
  /** 平台 AppID，服务端生成，用于路由、凭证与密钥 */
  app_id: string
  /** 开发者填写的模块 ID，写入主包 manifest 的 id */
  module_id?: string
  app_secret: string
  status: AppStatus
  created_at: string
}

/** 权限调用身份 */
export type ScopeIdentity = 'app' | 'user'

/** 敏感等级。对应接口 risk_level：1 普通 / 2 较高 / 3 敏感 */
export type SensitivityLevel = 'normal' | 'elevated' | 'sensitive'

/** 关联 API。type=api 展示为 [API]，type=event 展示为 [事件] */
export interface RelatedApi {
  name: string
  type: 'api' | 'event'
}

/** 已开通 / 审核状态（本期列表仅 approved） */
export type ScopeStatus = 'pending' | 'approved' | 'rejected'

/** 行主键：scope_id + 身份 */
export function appScopeId(
  scope_id: string,
  identity_type: ScopeIdentity,
): string {
  return `${scope_id}:${identity_type}`
}

/** scope_id 本身可含冒号，只按结尾的身份拆 */
export function parseAppScopeId(
  id: string,
): { scope_id: string; identity_type: ScopeIdentity } | null {
  if (id.endsWith(':app')) {
    return { scope_id: id.slice(0, -':app'.length), identity_type: 'app' }
  }
  if (id.endsWith(':user')) {
    return { scope_id: id.slice(0, -':user'.length), identity_type: 'user' }
  }
  return null
}

/** 应用已开通的 Scope 行 */
export interface AppScope {
  id: string
  scope_id: string
  name: string
  identity_type: ScopeIdentity
  category: string
  sensitivity_level: SensitivityLevel
  related_apis: RelatedApi[]
  doc_url?: string
  description?: string
  status: ScopeStatus
  /** 是否在当前线上版本快照里。关闭确认用这个，不在本地用版本列表重算 */
  in_live_snapshot: boolean
}

/** 平台可开通目录项 */
export interface ScopeCatalogItem {
  scope_id: string
  name: string
  identity_type: ScopeIdentity
  category: string
  sensitivity_level: SensitivityLevel
  related_apis: RelatedApi[]
  doc_url?: string
  description?: string
  /** 带 app_id 查询时：该应用是否已开通这一行。未返回按未开通 */
  granted: boolean
}

/** @deprecated 使用 AppScope */
export type ScopeItem = AppScope
/** @deprecated */
export type ScopeLevel = SensitivityLevel

/** 事件订阅身份 */
export type EventSubscribeType = 'tenant' | 'user'

/** 事件订阅项 */
export interface EventSubscription {
  id: string
  name: string
  event: string
  trigger: string
  enabled: boolean
  subscribeType: EventSubscribeType
}

/** 版本状态（已上线展示绑 `released`） */
export type VersionStatus =
  | 'developing'
  | 'reviewing'
  | 'rejected'
  | 'scheduled'
  | 'released'
  | 'delisted'
  | 'disabled'
  | 'archived'
  | 'rolling_back'

/** 版本列表操作人 */
export interface VersionOperator {
  nickname: string
  username: string
}

/** 分发方式（对齐 api distribution_type） */
export type DistributionType = 'self_built' | 'all_spaces'
/** @deprecated 使用 DistributionType */
export type DistributionMethod = DistributionType

/** 上架空间类别（分发方式=全部空间可用时） */
export type ListingCategory = 'workspace' | 'personal'

/** 清单 module.manifest.json.runtimes。desktop 桌面端，web 网页端 */
export type Runtime = 'desktop' | 'web'

/** 主包解析结果（只读，从主包提取） */
export interface MainPackageInfo {
  file_name: string
  size_mb: number
  version_code: string
  icon?: string
  /** 主包声明的运行平台 */
  platforms: Platform[]
  /** 是否需要分平台上传各运行平台包 */
  needs_platform_packages: boolean
  /**
   * 清单 runtimes。desktop 显示 PC 截图，web 显示移动端截图。
   * 缺省按 ["desktop"]。
   */
  runtimes?: Runtime[]
}

/** 各运行平台安装包 */
export interface PlatformPackage {
  platform: Platform
  file_name?: string
  size_mb?: number
  /** 包版本号（沿用历史包时为历史版本号） */
  package_version: string
  /** 是否沿用历史包 */
  is_historical: boolean
  upload_id?: string
  file_id?: string
  package_url?: string
  from_version_id?: string
  /** 直传 init 后、complete 前为 pending（详情会带 upload_id 但还没挂上）；缺省视为已就绪 */
  parse_status?: PlatformPackageParseStatus
  /** 仅前端：本次会话内上传接口已返回成功（接口暂未回 package_url 时兜底） */
  uploaded_locally?: boolean
}

export type PlatformPackageParseStatus = 'pending' | 'success' | 'failed'

/** 联系客服账号预览（对齐 search / draft.support_account） */
export interface SupportAccount {
  username: string
  nickname: string
  avatar_url: string
  space_name?: string
}

/** 展示信息 —— 单语言内容（对齐 localizations.*） */
export interface Localization {
  tagline: string
  description: string
  /** PC 截图（Windows/macOS 共用） */
  pc_screenshots: string[]
  /** 移动端截图（iOS/Android 共用） */
  mobile_screenshots: string[]
}

/** 展示信息（版本级，随版本送审） */
export interface DisplayInfo {
  /** 支持语言（内容语言代码，如 en / zh-CN） */
  supported_languages: string[]
  default_language: string
  /** 应用分类，最多 2 个（API categories） */
  categories: string[]
  /** 应用标签，最多 5 个 */
  tags: string[]
  website_url?: string
  privacy_policy_url?: string
  terms_url?: string
  support_account?: SupportAccount
  /** 按语言的本地化内容 */
  localizations: Record<string, Localization>
}

/** 应用固定信息（应用级，首版审核通过后只读） */
export interface AppFixedInfo {
  distribution_type: DistributionType
  /** 上架空间类别（API listing_space_types） */
  listing_space_types: ListingCategory[]
  /** 所属空间名称（分发方式=空间自建，只读展示） */
  owner_workspace?: string
  /** 所属空间 id（调 org 选人 / 创建入口写入） */
  owner_space_id?: string
  /**
   * 可使用范围：全员 / 指定（部门+成员）。
   * 缺省时按 departments+members 是否为空推断。
   */
  available_type?: 'all' | 'specified'
  /** 可使用成员 ID 列表（API available_members.members = org_member.id） */
  eligible_members: string[]
  /** 可使用部门 ID 列表（API available_members.departments） */
  eligible_departments: string[]
  /** 首版审核通过后为 true */
  distribution_locked?: boolean
}

/** 版本提交时权限快照。只含安装包权限，OpenAPI 在 permission_diff */
export interface ScopeSnapshot {
  approved: string[]
}

/** 相对上一已发布版的一条权限。name 按当前语言，可空 */
export interface PermissionDiffItem {
  scope: string
  name?: string
  sensitivity?: string
  /** 详情里固定 approved，pending 不会出现 */
  status?: string
  /** 0 安装包权限，1 应用身份，2 用户身份 */
  token_type?: number
}

/** 版本详情里的权限对比。没提交时 OpenAPI 读当前开通记录，提交后读冻结列 */
export interface PermissionDiff {
  /** 安装包和 OpenAPI 都没有增删 */
  unchanged: boolean
  added: PermissionDiffItem[]
  removed: PermissionDiffItem[]
  unchanged_items: PermissionDiffItem[]
}

/** 相对上一已发布版的一条事件。name 按当前语言，可空 */
export interface EventDiffItem {
  event_code: string
  name?: string
  /**
   * tenant 应用身份；user 用户身份。
   * 详情 event_diff 当前可不带；缺省按 tenant 归入应用身份事件。
   */
  subscribe_type?: 'tenant' | 'user'
}

/** 版本详情里的事件对比。草稿读当前订阅，提交后读冻结列 */
export interface EventDiff {
  unchanged: boolean
  added: EventDiffItem[]
  removed: EventDiffItem[]
  unchanged_items: EventDiffItem[]
}

/** 版本信息（对齐 GET /versions 列表项 + 版本详情字段） */
export interface AppVersion {
  id: string
  version: string
  status: VersionStatus
  platforms: Platform[]
  /** 本版更新的端（服务端 uploaded_platforms）；无则从 platform_packages 推导 */
  uploaded_platforms?: Platform[]
  released_at: string | null
  last_operated_at: string
  scheduled_at: string | null
  operator?: VersionOperator

  // ── 版本详情字段（抽屉表单） ──
  main_package?: MainPackageInfo
  platform_packages?: PlatformPackage[]
  /** 更新日志，按内容语言代码；首版不传 */
  changelog?: Record<string, string>
  app_fixed_info?: AppFixedInfo
  display_info?: DisplayInfo
  scope_snapshot?: ScopeSnapshot
  /** 相对上一已发布版的权限对比；列表接口通常不带，打开详情后才有 */
  permission_diff?: PermissionDiff
  /** 相对上一已发布版的事件对比；列表接口通常不带，打开详情后才有 */
  event_diff?: EventDiff
  review_comment?: string
  /** 是否为该应用首版 */
  is_first_version?: boolean
}

/** 上传任务状态 */
export type UploadTaskStatus =
  | 'uploading'
  | 'parsing'
  | 'success'
  | 'failed'
  | 'parse_failed'
  | 'waiting'

/** 悬浮浮层的单个上传任务 */
export interface UploadTask {
  id: string
  kind: 'main' | 'platform'
  platform?: Platform
  file_name: string
  size_mb: number
  loaded_mb: number
  progress: number
  speed?: string
  remaining_sec?: number
  /** 可重新上传时保留 File，供 retry */
  file?: File
  /** 发起上传时的目标草稿；重试不能只靠 store 里的草稿 id */
  app_id?: string
  version_id?: string
  /** 失败时的接口/网络错误文案，供 toast */
  error_message?: string
  status: UploadTaskStatus
}

/** 安全设置里的一条规则。id 为空表示还没有服务端行 id。 */
export interface SecurityRule {
  id: string
  value: string
}

/** 安全设置（草稿，发布版本后生效）。页面上的真实规则列表不进这份 store。 */
export interface SecuritySettings {
  redirect_allowlist: string[]
  ip_allowlist: string[]
  h5_trusted_domains: string[]
}

/** 协作成员 */
export interface Collaborator {
  id: string
  name: string
  email: string
  avatar: string
  role: 'owner' | 'developer'
}

/** 当前登录用户 */
export interface UserInfo {
  id: string
  name: string
  email: string
  avatar: string
  /** 系统分配的开发者编号，格式 DEV-XXXXXXXX */
  developer_id?: string
  bio?: string
  website?: string
}

/** 顶栏切换空间列表项，数据来自组织列表接口 */
export type SpaceType = 'personal' | 'workspace'

export interface SpaceItem {
  id: string
  name: string
  logo?: string
  type: SpaceType
  /** 工作空间角色展示，个人空间可不传 */
  role?: 'owner' | 'admin' | 'member'
}
